import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { X, EyeOff } from '@/components/icons';
import { PrntrCard } from './ui/prntr-input';
import { TextRow, TextareaField } from './ui/prntr-fields';
import { Button } from './ui/button';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from './ui/drawer';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
} from './ui/alert-dialog';
import { toast } from 'sonner';
import { track, EVENTS } from '@/utils/analytics';
import { useQuery } from '@pronteer/convex/react';
import { api } from '@pronteer/convex/api';
import type { Id } from '@pronteer/convex/dataModel';
import { useIsAdmin } from '../hooks/useIsCompanyAdmin';
import { useCrewGetAll } from '../lib/hooks/crew';
import { useMarkAnnouncementAsRead } from '../lib/convex-hooks';
import {
  useAnnouncementCreate,
  useAnnouncementUpdate,
  useAnnouncementUnpublish,
} from '../lib/hooks/announcement';
import { useCurrentCrewMember } from '../hooks/useCurrentCrewMember';

export type ModeType = 'create' | 'edit';

interface AnnouncementDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  announcementId?: Id<'announcements'>; // For edit mode
}

const announcementFormSchema = z.object({
  title: z
    .string()
    .min(1, 'Title is required')
    .max(100, 'Title must be 100 characters or less')
    .default(''),

  message: z
    .string()
    .min(1, 'Message is required')
    .max(500, 'Message must be 500 characters or less')
    .default(''),
});

type AnnouncementFormData = z.infer<typeof announcementFormSchema>;

export function AnnouncementDrawer({
  open,
  onOpenChange,
  onSuccess,
  announcementId,
}: AnnouncementDrawerProps) {
  const { isAdmin } = useIsAdmin();
  const mode: ModeType = announcementId ? 'edit' : 'create';
  const [unpublishing, setUnpublishing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isUnpublishDialogOpen, setIsUnpublishDialogOpen] = useState(false);

  // Get current crew member for companyId
  const { data: currentCrew } = useCurrentCrewMember();

  // Fetch crew list to get user IDs for broadcast
  const { data: allCrew } = useCrewGetAll();

  // Fetch announcement data for edit mode
  const announcementData = useQuery(
    api.announcements.getById,
    open && announcementId ? { id: announcementId } : 'skip'
  );

  // Writes go through the API (which sends the push); reads stay on Convex.
  const { mutateAsync: createAnnouncement } = useAnnouncementCreate();
  const { mutateAsync: updateAnnouncementMutation } = useAnnouncementUpdate();
  const { mutateAsync: unpublishAnnouncementMutation } = useAnnouncementUnpublish();
  const markAsRead = useMarkAnnouncementAsRead();

  const { watch, reset, formState, control } = useForm<AnnouncementFormData>({
    resolver: zodResolver(announcementFormSchema),
    mode: 'onChange',
    defaultValues: {
      title: '',
      message: '',
    },
  });

  const title = watch('title') || '';
  const message = watch('message') || '';

  // Mark announcement as read when drawer opens in edit mode
  useEffect(() => {
    if (open && announcementId) {
      markAsRead({ id: announcementId, crewId: currentCrew!.id });
    }
  }, [open, announcementId]);

  // Reset form based on mode
  useEffect(() => {
    if (open) {
      if (mode === 'create') {
        reset({
          title: '',
          message: '',
        });
      } else if (mode === 'edit' && announcementData) {
        reset({
          title: announcementData.title,
          message: announcementData.message,
        });
      }
    }
  }, [open, mode, announcementData, reset]);
  const handleClose = () => {
    reset();
    onOpenChange(false);
  };

  const handleSave = async () => {
    if (!formState.isValid) {
      return;
    }

    setSaving(true);
    try {
      if (mode === 'create') {
        // Check for companyId
        if (!currentCrew?.companyId) {
          toast.error('Unable to determine company');
          setSaving(false);
          return;
        }

        // Extract crew IDs from crew data
        const crewIds = (allCrew || [])
          .map(crew => crew.id)
          .filter(Boolean); // Filter out any null/undefined

        if (crewIds.length === 0) {
          toast.error('No crew members found to send announcement to');
          setSaving(false);
          return;
        }

        // Recipients are derived server-side (all active company crew); the
        // client only sends the content.
        await createAnnouncement({ title, message });
        track(EVENTS.ANNOUNCEMENT_SENT, { recipientCount: crewIds.length })
        toast.success('Announcement sent to all crew members');
      } else if (mode === 'edit' && announcementId) {
        await updateAnnouncementMutation({
          id: announcementId,
          title,
          message,
        });
        toast.success('Announcement updated');
      }
      onSuccess();
      handleClose();
    } catch (error: any) {
      console.error('Error saving announcement:', error);
      toast.error('Failed to save announcement');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    handleClose();
  };

  const handleConfirmUnpublish = async () => {
    if (!announcementId) return;

    setUnpublishing(true);
    try {
      await unpublishAnnouncementMutation({ id: announcementId });
      toast.success('Announcement unpublished');
      setIsUnpublishDialogOpen(false);
      onSuccess();
      onOpenChange(false);
    } catch (error: any) {
      console.error('Error unpublishing announcement:', error);
      toast.error('Failed to unpublish announcement');
    } finally {
      setUnpublishing(false);
    }
  };

  const getTitle = () => {
    return mode === 'create' ? 'New Announcement' : 'Edit Announcement';
  };

  return (
    <>
    <Drawer open={open} onOpenChange={handleClose}>
      <DrawerContent className="bg-surface-L0 max-h-[85dvh] flex flex-col">
        <DrawerHeader className="flex justify-between items-center p-4">
          <DrawerTitle className="font-primary font-semibold text-lg text-content-primary-bold">
            {getTitle()}
          </DrawerTitle>
          <DrawerClose asChild>
            <Button variant="secondary" size="icon" roundness="round" className="size-8">
              <X className="w-4 h-4" />
            </Button>
          </DrawerClose>
        </DrawerHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-2">
          <div className="flex flex-col gap-3 p-2">
            {/* Title Field */}
            <div className="flex flex-col gap-1">
              <PrntrCard>
                <TextRow
                  control={control}
                  name="title"
                  label="Title"
                  placeholder="Add"
                  nullable={false}
                  error={formState.errors.title?.message}
                />
              </PrntrCard>
              <p className="text-xs text-content-primary-subtle">{title.length}/100 characters</p>
            </div>

            {/* Message Field */}
            <div className="flex flex-col gap-1">
              <TextareaField
                control={control}
                name="message"
                label="Message"
                placeholder="Enter details"
                error={formState.errors.message?.message}
              />
              <p className="text-xs text-content-primary-subtle">{message.length}/500 characters</p>
            </div>
          </div>
        </div>

        {/* Footer with Cancel and Save buttons */}
        <DrawerFooter>
          <div className="flex gap-2 justify-end">
            <Button variant="secondary" size="mini" className="flex-1" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="mini"
              className="flex-1"
              onClick={handleSave}
              disabled={!formState.isValid || saving}
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>

    <AlertDialog open={isUnpublishDialogOpen} onOpenChange={setIsUnpublishDialogOpen}>
      <AlertDialogContent className="p-8">
        <AlertDialogDescription className="text-center text-sm text-content-primary-subtle leading-5">
          Unpublish this announcement? Crew members will no longer see it.
        </AlertDialogDescription>
        <div className="flex flex-col gap-2 mt-2">
          <Button
            variant="destructive"
            size="mini"
            className="w-full"
            onClick={handleConfirmUnpublish}
            disabled={unpublishing}
          >
            <EyeOff className="size-4" />
            Unpublish
          </Button>
          <Button
            variant="secondary"
            size="mini"
            className="w-full"
            onClick={() => setIsUnpublishDialogOpen(false)}
            disabled={unpublishing}
          >
            Cancel
          </Button>
        </div>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
